import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { AlertCircle, Radio } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_standby-alert/artifacts/p05naeh5_ChatGPT%20Image%20Apr%206%2C%202026%2C%2009_47_10%20PM.png";

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900/95 border-zinc-700 backdrop-blur">
        <CardContent className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="OSMC Communication Division" 
              className="w-40 h-40 object-contain"
              data-testid="company-logo"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight mb-2">
              DISPATCH SYSTEM
            </h1>
            <p className="text-zinc-400 text-sm flex items-center justify-center gap-2">
              <Radio className="w-4 h-4 text-blue-500" />
              Medical Standby Operations
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div 
                className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-sm flex items-center gap-2"
                data-testid="login-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-300">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500 focus:ring-blue-500 focus:border-blue-500"
                data-testid="login-username-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500 focus:ring-blue-500 focus:border-blue-500"
                data-testid="login-password-input"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-sm transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-zinc-500 text-xs">
              OSMC Communication Division &copy; {new Date().getFullYear()}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
